import { ecosystemProducts, hiddenProductIds } from '@/mocks/homeData';
import { useInView } from '@/hooks/useInView';

const visibleProducts = ecosystemProducts.filter((product) => !hiddenProductIds.includes(product.id));

function EcosystemNode({ product, delay }: { product: typeof ecosystemProducts[0]; delay: number }) {
  const [ref, inView] = useInView<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`group p-4 md:p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300/50 hover:shadow-md transition-all duration-500 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className={`w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-lg mb-3 ${product.color}`}>
        <i className={`${product.icon} text-lg md:text-xl`} />
      </div>
      <h4 className="text-sm md:text-base font-semibold text-foreground-900 mb-1">
        {product.name}
      </h4>
      <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
        {product.tagline}
      </p>
    </div>
  );
}

export default function EcosystemSection() {
  const [titleRef, titleInView] = useInView<HTMLDivElement>();

  return (
    <section id="products" className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24 bg-gradient-to-b from-background-100 to-background-50">
      {/* Header */}
      <div
        ref={titleRef}
        className={`max-w-3xl mb-12 md:mb-16 transition-all duration-700 ${
          titleInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-100 rounded-full mb-5">
          <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
          <span className="text-xs font-medium text-primary-700 uppercase tracking-wider">Ecosystem</span>
        </div>
        <h2
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4"
          style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}
        >
          The Ardeun Biometric Platform
        </h2>
        <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
          A unified ecosystem of biometric solutions that work together to secure every digital interaction — from email to enterprise access.
        </p>
      </div>

      {/* Ecosystem Layout */}
      <div className="flex flex-col">
        {/* Top centered product */}
        {visibleProducts[0] && (
          <div className="flex justify-center mb-6 lg:mb-8">
            <div className="w-full max-w-sm">
              <EcosystemNode product={visibleProducts[0]} delay={0} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-start">
          {/* Left Column */}
          <div className="space-y-4 md:space-y-5">
            {visibleProducts.slice(1, 3).map((product, i) => (
              <EcosystemNode key={product.id} product={product} delay={(i + 1) * 100} />
            ))}
          </div>

          {/* Center Hub */}
          <div className="flex items-center justify-center py-6 lg:py-0">
            <div className="relative w-48 h-48 md:w-56 md:h-56 lg:w-64 lg:h-64">
              {/* Outer ring */}
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-primary-300/40 animate-spin" style={{ animationDuration: '20s' }} />
              <div className="absolute inset-3 rounded-full border border-primary-200/30" />

              {/* Center card */}
              <div className="absolute inset-6 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex flex-col items-center justify-center text-background-50 shadow-lg shadow-primary-500/20">
                <div className="w-12 h-12 rounded-xl bg-background-50/20 flex items-center justify-center mb-2">
                  <i className="ri-fingerprint-line text-2xl" />
                </div>
                <span className="text-sm font-bold font-heading text-center leading-tight">Ardeun</span>
                <span className="text-[10px] text-background-200 text-center">Biometrics Corp</span>
              </div>

              {/* Connection dots */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-primary-400" />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-accent-400" />
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-secondary-400" />
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-primary-400" />
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4 md:space-y-5">
            {visibleProducts.slice(3).map((product, i) => (
              <EcosystemNode key={product.id} product={product} delay={(i + 4) * 100} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}